import React, { useMemo } from 'react';
import {
  ClassEnrollmentStatus,
  CourseEnrollmentStatus,
  GetViewerLearningDetailsQuery,
  ProofOfCompletionType,
} from '@sandtech-hub-monorepo/ehub-client';
import { CalendarIcon, Check, CircleX, DotIcon } from 'lucide-react';
import { ReactComponent as DeferIcon } from '../../../assets/Icons/defer-icon.svg';
import { ReactComponent as PauseIcon } from '../../../assets/Icons/pause-icon.svg';
import { differenceInWeeks } from 'date-fns';
import { formatDate } from '../../../dateFormat';
import { cn } from '@sandtech-hub-monorepo/ui-kit';
import { getIconFromAbbreviation } from '../../../../programUtils';
import { PlannedCourseButton } from './PlannedCourseButton';
import { getExplanationTextByProofOfCompletionType } from '../../ProofOfCompletion/util';
import { CourseBadgeCard } from './CourseBadgeCard';

type PlannedCourseViewType = NonNullable<
  NonNullable<GetViewerLearningDetailsQuery['viewer']['programEnrollments']>
>[number]['plannedCourses'][number];

type PlannedCourseViewProps = {
  plannedCourse: PlannedCourseViewType;
  variant?: 'default' | 'university';
};

export const PlannedCourseView = ({
  plannedCourse,
  variant = 'default',
}: PlannedCourseViewProps) => {
  const isUniversity = variant === 'university';
  const badge = plannedCourse.details.issuedBadge;

  const statusRender = (s: ClassEnrollmentStatus) => (
    <>
      {s === ClassEnrollmentStatus.Deferred && (
        <div className="flex gap-1 items-center mr-auto">
          <DeferIcon width={24} height={24} color={'#F79009'} />
          <span className="text-sm text-[#F79009]">Deferred</span>
        </div>
      )}
      {s === ClassEnrollmentStatus.LeaveOfAbsence && (
        <div className="flex gap-1 items-center mr-auto">
          <PauseIcon width={24} height={24} color={'#F79009'} />
          <span className="text-sm text-[#F79009]">Paused</span>
        </div>
      )}
      {s === ClassEnrollmentStatus.Graduated && (
        <div className="flex gap-0.5 items-center mr-auto">
          <Check width={24} height={24} className="stroke-success" />
          <span className="text-sm text-success">Completed</span>
        </div>
      )}
      {s === ClassEnrollmentStatus.Failed && (
        <div className="flex gap-0.5 items-center mr-auto">
          <CircleX width={24} height={24} className="stroke-destructive" />
          <span className="text-sm text-destructive">Failed</span>
        </div>
      )}
    </>
  );

  const isWaitingForProofOfCompletion = useMemo(() => {
    if (
      plannedCourse.enrollment?.currentClass?.class.proofOfCompletionType ===
      ProofOfCompletionType.None
    ) {
      return false;
    }

    if (
      plannedCourse.enrollment?.currentClass?.status !==
      ClassEnrollmentStatus.Ongoing
    ) {
      return false;
    }

    if (plannedCourse.enrollment?.currentClass?.proofOfCompletion) {
      return false;
    }

    const startDateOfSubmittingProofOfCompletion = plannedCourse.enrollment
      ?.currentClass?.class.requiresProofOfCompletionAfter
      ? new Date(
          plannedCourse.enrollment?.currentClass?.class.requiresProofOfCompletionAfter
        )
      : undefined;

    if (!startDateOfSubmittingProofOfCompletion) {
      return false;
    }

    return (
      new Date().getTime() - startDateOfSubmittingProofOfCompletion.getTime() >
      0
    );
  }, []);

  // Falls back to the courseEnrollment-level status when there is no
  // classEnrollment — e.g. an admin-back-filled graduation that never had an
  // LMS class enrollment created. currentClass is also null when every attempt
  // was cancelled (the resolver hides those), which is why only GRADUATED is
  // mapped here: a course left FAILED by a withdrawal whose attempts were all
  // cancelled must render as not started, with no status chip.
  const effectiveStatus: ClassEnrollmentStatus | undefined =
    plannedCourse.enrollment?.currentClass?.status ??
    (plannedCourse.enrollment?.status === CourseEnrollmentStatus.Graduated
      ? ClassEnrollmentStatus.Graduated
      : undefined);

  const showBadge =
    !!badge && effectiveStatus === ClassEnrollmentStatus.Graduated;

  const isSelfPacedShortCourse =
    plannedCourse.details.type === 'self-paced-short-course';

  return (
    <div className={cn('flex', isUniversity ? 'p-4 gap-2' : 'gap-6 my-4 sm:my-3')}>
      <div>
        {getIconFromAbbreviation(
          plannedCourse.details.abbreviation,
          isUniversity ? 'w-8 h-auto' : ''
        )}
      </div>
      <div className="w-full flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-4 md:gap-2 justify-between">
          <div className="flex flex-col gap-2 md:gap-0">
            {isUniversity ? (
              <span className="font-semibold text-base text-heading">
                {plannedCourse.details.abbreviation && (
                  <>{plannedCourse.details.abbreviation}: </>
                )}
                {plannedCourse.details.name}
                {effectiveStatus && statusRender(effectiveStatus)}
              </span>
            ) : (
              <>
                <span className="block lg:hidden text-lg font-semibold text-heading">
                  {plannedCourse.details.name}
                  {effectiveStatus && statusRender(effectiveStatus)}
                </span>
                <div className="hidden lg:flex gap-3 text-xl font-semibold text-foreground justify-between text-heading">
                  <span>{plannedCourse.details.name}</span>
                  {effectiveStatus && statusRender(effectiveStatus)}
                </div>
              </>
            )}
            <p className="text-sm text-popover-foreground">
              {plannedCourse.details.description}
            </p>
            {plannedCourse.enrollment &&
              plannedCourse.enrollment.currentClass &&
              (isWaitingForProofOfCompletion || !isSelfPacedShortCourse) && (
                <div
                  className={cn(
                    'flex flex-wrap gap-1 text-sm text-foreground font-medium',
                    isWaitingForProofOfCompletion ? 'items-start mt-1' : 'items-center'
                  )}
                >
                  <CalendarIcon size={16} className="text-popover-foreground" />
                  {isWaitingForProofOfCompletion ? (
                    <span className="flex-1">
                      {getExplanationTextByProofOfCompletionType(
                        plannedCourse.enrollment?.currentClass?.class
                          .proofOfCompletionType!
                      )}
                    </span>
                  ) : (
                    <>
                      <span>
                        {formatDate(
                          plannedCourse.enrollment.currentClass.class.startDate
                        )}
                      </span>
                      <DotIcon />
                      <span>
                        {differenceInWeeks(
                          plannedCourse.enrollment.currentClass.class.endDate,
                          plannedCourse.enrollment.currentClass.class.startDate
                        )}{' '}
                        weeks
                      </span>
                    </>
                  )}
                </div>
              )}
          </div>
          <PlannedCourseButton
            status={plannedCourse.enrollment?.currentClass?.status}
            link={
              plannedCourse.enrollment?.currentClass?.lmsClassLocation
                ?.courseLink ?? '/'
            }
          />
        </div>
        {showBadge && (
          <CourseBadgeCard
            courseName={plannedCourse.details.name}
            badge={badge!}
          />
        )}
      </div>
    </div>
  );
};
